import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { DEFAULT_WA_MESSAGES, WA_LABELS } from "@/config/site";
import { BookOpen, ArrowRight, Download, Mail, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { usePageCms } from "@/lib/siteSettings";
import { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";

function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${API}${url.slice(4)}`;
  return url;
}

const CAT_IDS = ["all", "rehab", "running", "strength", "recovery", "prevention"];
const ARTICLE_IDS = ["acl-return", "runners-knee", "low-back", "s-and-c-101", "recovery-truths", "shoulder-impingement", "hamstring-prevention", "return-run"];
const DOWNLOAD_IDS = ["pain-diary", "rtp-checklist", "warmup"];

export default function Resources() {
  const { t } = useTranslation();
  const cms = usePageCms("resources");
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [cmsItems, setCmsItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/cms/resources`)
      .then((r) => setCmsItems(r.data || []))
      .catch(() => setCmsItems([]));
  }, []);

  const list = ARTICLE_IDS.filter((id) => {
    if (filter === "all") return true;
    const cat = t(`pub.resources.articles.${id}.cat`, { lng: "en" }).toLowerCase();
    return cat === t(`pub.resources.cats.${filter}`, { lng: "en" }).toLowerCase();
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.consent) {
      toast.error(t("pub.resources.leadValidateErr"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/inquiry`, {
        name: form.name, email: form.email,
        message: "Requested to be added to the Functional LAB resources & tips list.",
        topic: "resources_subscribe", consent: form.consent, source: "resources_page",
      });
      setSent(true);
      toast.success(t("pub.resources.leadSuccessToast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen fl-page" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />

      {/* Hero */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("hero", "eyebrow", t("pub.resources.eyebrow"))}</div>
          <h1 className="fl-heading text-5xl md:text-6xl leading-none">
            {cms.get("hero").heading ? cms.get("hero").heading : (<>{t("pub.resources.h1a")} <span className="text-[color:var(--fl-orange)]">{t("pub.resources.h1b")}</span> {t("pub.resources.h1c")} <span className="text-[color:var(--fl-orange)]">{t("pub.resources.h1d")}</span> {t("pub.resources.h1e")}</>)}
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "var(--fl-muted)" }}>
            {cms.text("hero", "body", t("pub.resources.subtitle"))}
          </p>
        </div>
      </section>
      )}

      {/* Content */}
      <section className="py-24 md:py-32 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
        {/* CMS-driven articles (admin managed) */}
        {cmsItems.length > 0 && (
          <div className="mb-16" data-testid="res-cms-list">
            <div className="fl-label text-[color:var(--fl-orange)] mb-3">Latest from the LAB</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cmsItems.map((it) => {
                const isComingSoon = it.status === "coming_soon";
                return (
                  <article key={it.id} className="fl-card p-6 flex flex-col" data-testid={`res-cms-${it.id}`}>
                    {it.cover_image_url && (
                      <div className="rounded-md overflow-hidden mb-4 aspect-video bg-white/5">
                        <img src={resolveMediaUrl(it.cover_image_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="fl-label" style={{ color: "var(--fl-orange)" }}>{it.category || "Article"}</div>
                      {isComingSoon && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ color: "var(--fl-orange)", background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, transparent)" }}>
                          <Clock size={10} /> Coming soon
                        </span>
                      )}
                    </div>
                    <h3 className="fl-heading text-lg mt-2 leading-snug">{it.title}</h3>
                    {it.excerpt && <p className="mt-3 text-sm flex-1" style={{ color: "var(--fl-muted)" }}>{it.excerpt}</p>}
                    <div className="mt-4 flex items-center justify-between gap-2">
                      {!isComingSoon && it.download_url ? (
                        <a href={it.download_url} target="_blank" rel="noopener noreferrer" className="fl-btn-ghost text-xs px-3 py-1.5" data-testid={`res-cms-dl-${it.id}`}>
                          <Download size={12} /> Download
                        </a>
                      ) : (
                        <span className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>{isComingSoon ? "Publishing soon" : "Read on request"}</span>
                      )}
                      <WhatsAppCTA
                        message={`${DEFAULT_WA_MESSAGES.resources} My question is about: ${it.title}`}
                        testid={`res-cms-wa-${it.id}`}
                        variant="soft"
                        size="sm"
                      >
                        {t("pub.resources.askUs")}
                      </WhatsAppCTA>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {CAT_IDS.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`res-filter-${c}`}
              className="text-xs px-3 py-1.5 rounded-full border transition-colors"
              style={{
                borderColor: filter === c ? "var(--fl-orange)" : "var(--fl-border-2)",
                color: filter === c ? "var(--fl-orange)" : "var(--fl-muted)",
                background: filter === c ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent",
              }}
            >
              {t(`pub.resources.cats.${c}`)}
            </button>
          ))}
        </div>

        {/* Article grid */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {list.map((id) => (
            <article key={id} className="fl-card p-6 flex flex-col" data-testid={`res-article-${id}`}>
              <div className="fl-label" style={{ color: "var(--fl-orange)" }}>{t(`pub.resources.articles.${id}.cat`)}</div>
              <h3 className="fl-heading text-lg mt-2 leading-snug">{t(`pub.resources.articles.${id}.title`)}</h3>
              <p className="mt-3 text-sm flex-1" style={{ color: "var(--fl-muted)" }}>{t(`pub.resources.articles.${id}.abstract`)}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>{t("pub.resources.comingSoon")}</span>
                <WhatsAppCTA
                  message={`${DEFAULT_WA_MESSAGES.resources} My question is about: ${t(`pub.resources.articles.${id}.title`, { lng: "en" })}`}
                  testid={`res-wa-${id}`}
                  variant="soft"
                  size="sm"
                >
                  {t("pub.resources.askUs")}
                </WhatsAppCTA>
              </div>
            </article>
          ))}
        </div>

        {/* Downloads strip */}
        <div className="mb-16">
          <div className="fl-label text-[color:var(--fl-orange)] mb-3">{t("pub.resources.downloadsEyebrow")}</div>
          <div className="grid md:grid-cols-3 gap-4">
            {DOWNLOAD_IDS.map((id) => (
              <div key={id} className="fl-card p-5" data-testid={`res-dl-${id}`}>
                <Download size={18} className="text-[color:var(--fl-orange)]" />
                <div className="fl-heading text-base mt-3">{t(`pub.resources.downloads.${id}.title`)}</div>
                <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>{t(`pub.resources.downloads.${id}.body`)}</div>
                <WhatsAppCTA
                  message={`Hi Functional LAB, could you send me the "${t(`pub.resources.downloads.${id}.title`, { lng: "en" })}" resource?`}
                  testid={`res-dl-wa-${id}`}
                  variant="soft"
                  size="sm"
                  className="mt-4"
                >
                  {t("pub.resources.downloadRequest")}
                </WhatsAppCTA>
              </div>
            ))}
          </div>
        </div>

        {/* Lead capture */}
        <div className="fl-card p-8 md:p-10 relative overflow-hidden max-w-4xl mx-auto" data-testid="res-leadcapture">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,69,0,0.14), transparent 70%)" }} />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <BookOpen size={22} className="text-[color:var(--fl-orange)]" />
              <h3 className="fl-heading text-3xl mt-3">{t("pub.resources.leadTitle")}</h3>
              <p className="mt-3 text-sm" style={{ color: "var(--fl-muted)" }}>{t("pub.resources.leadDesc")}</p>
            </div>
            {sent ? (
              <div className="text-sm flex items-start gap-2" style={{ color: "var(--fl-muted)" }} data-testid="res-lead-success">
                <CheckCircle2 className="text-[#22C55E] mt-0.5 flex-shrink-0" size={18} />
                <span>{t("pub.resources.leadSuccess")}</span>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="res-lead-form">
                <input type="text" placeholder={t("pub.resources.leadName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fl-input w-full text-sm px-3 py-2 rounded-md" data-testid="res-lead-name" required />
                <input type="email" placeholder={t("pub.resources.leadEmail")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fl-input w-full text-sm px-3 py-2 rounded-md" data-testid="res-lead-email" required />
                <label className="flex items-start gap-2 text-xs" style={{ color: "var(--fl-muted)" }}>
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="res-lead-consent" className="mt-0.5" />
                  {t("pub.resources.leadConsent")}
                </label>
                <button type="submit" disabled={submitting} className="fl-btn text-sm w-full justify-center" data-testid="res-lead-submit">
                  <Mail size={14} /> {submitting ? t("pub.resources.leadSubmitting") : t("pub.resources.leadSubmit")}
                </button>
              </form>
            )}
          </div>
        </div>

        </div>
      </section>

      {/* Final CTA */}
      {cms.isVisible("finalCta") && (
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-[#1A1A1A] via-[#170f0d] to-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="fl-heading text-3xl md:text-4xl">{cms.text("finalCta", "heading", t("pub.resources.ctaTitle"))}</h3>
          <p className="mt-3 text-sm" style={{ color: "var(--fl-muted)" }}>{cms.text("finalCta", "body", t("pub.resources.ctaBody"))}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/contact" className="fl-btn text-sm" data-testid="res-cta-contact">{cms.text("finalCta", "ctaLabel", t("pub.resources.ctaContact"))} <ArrowRight size={14} /></a>
            <WhatsAppCTA message={DEFAULT_WA_MESSAGES.bookAssessment} testid="res-cta-wa" size="md">
              {t("pub.wa.book")}
            </WhatsAppCTA>
          </div>
        </div>
      </section>
      )}
      <PublicFooter />
    </div>
  );
}
